import type { PDFFont } from 'pdf-lib';
import { PDFDocument, rgb } from 'pdf-lib';
import { embedBodyFonts, embedStandardFallbackFonts } from './pdfFonts';
import type { PdfExportQuality } from './pdfExportQuality';
import { layoutForQuality } from './pdfExportQuality';
import type { PageDims } from './pdfPageDims';

export interface CodePdfOptions {
  quality: PdfExportQuality;
  pageDims: PageDims;
  languageExtension: string;
  theme: 'light' | 'dark';
  lineNumbers: boolean;
  wrapLongLines: boolean;
  syntaxHighlight: boolean;
}

function drawFooter(page: ReturnType<PDFDocument['getPage']>, index: number, total: number, font: PDFFont, margin: number): void {
  const label = `Page ${index + 1} of ${total}`;
  const size = 8.5;
  const w = font.widthOfTextAtSize(label, size);
  const pw = page.getWidth();
  page.drawText(label, {
    x: pw - margin - w,
    y: margin * 0.55,
    size,
    font,
    color: rgb(0.55, 0.55, 0.58),
  });
}

function applyFooters(doc: PDFDocument, font: PDFFont, margin: number): void {
  doc.getPages().forEach((p, i, arr) => drawFooter(p, i, arr.length, font, margin));
}

const JS_KEYWORDS = new Set([
  'function',
  'return',
  'const',
  'let',
  'var',
  'if',
  'else',
  'for',
  'while',
  'async',
  'await',
  'import',
  'export',
  'default',
  'from',
  'class',
  'extends',
  'new',
  'try',
  'catch',
  'throw',
  'typeof',
  'interface',
  'type',
  'enum',
]);

function tokenColor(token: string, ext: string, theme: 'light' | 'dark', highlight: boolean) {
  const base = theme === 'dark' ? rgb(0.92, 0.93, 0.95) : rgb(0.12, 0.13, 0.16);
  if (!highlight) return base;

  const lower = ext.toLowerCase();
  const isJsFamily = ['js', 'jsx', 'ts', 'tsx', 'mjs', 'cjs'].includes(lower);
  if (isJsFamily && JS_KEYWORDS.has(token)) {
    return theme === 'dark' ? rgb(0.62, 0.78, 1) : rgb(0.15, 0.35, 0.72);
  }
  if (/^["'`].*["'`]$/.test(token)) {
    return theme === 'dark' ? rgb(0.65, 0.88, 0.65) : rgb(0.12, 0.48, 0.22);
  }
  if (/^\d+$/.test(token) || /^0x[0-9a-f]+$/i.test(token)) {
    return theme === 'dark' ? rgb(1, 0.78, 0.48) : rgb(0.72, 0.35, 0.05);
  }
  if (token.startsWith('//') || token.startsWith('#')) {
    return theme === 'dark' ? rgb(0.58, 0.62, 0.68) : rgb(0.42, 0.45, 0.5);
  }
  return base;
}

function wrapLine(text: string, font: PDFFont, size: number, maxW: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const trial = line ? `${line} ${w}` : w;
    if (font.widthOfTextAtSize(trial, size) <= maxW) line = trial;
    else {
      if (line) lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [''];
}

export async function convertSourceTextToPdf(source: string, opts: CodePdfOptions): Promise<Uint8Array> {
  const layout = layoutForQuality(opts.quality);
  const doc = await PDFDocument.create();
  let fonts;
  try {
    fonts = await embedBodyFonts(doc);
  } catch {
    fonts = await embedStandardFallbackFonts(doc);
  }
  const mono = fonts.mono;

  const { widthPt, heightPt } = opts.pageDims;
  let page = doc.addPage([widthPt, heightPt]);
  let y = heightPt - layout.margin;

  const gutter = opts.lineNumbers ? 40 : 0;
  const maxW = widthPt - 2 * layout.margin - gutter;
  const size = layout.metaSize * 0.98;
  const lh = size * 1.35;

  const paintBg = () => {
    const bg = opts.theme === 'dark' ? rgb(0.07, 0.08, 0.1) : rgb(1, 1, 1);
    page.drawRectangle({ x: 0, y: 0, width: widthPt, height: heightPt, color: bg });
  };
  paintBg();

  const newPage = () => {
    page = doc.addPage([widthPt, heightPt]);
    paintBg();
    y = heightPt - layout.margin;
  };

  const ensureSpace = (need: number) => {
    if (y - need < layout.margin + layout.footerReserved) {
      newPage();
    }
  };

  const rawLines = source.replace(/\r\n/g, '\n').split('\n');

  rawLines.forEach((rawLine, idx) => {
    const safeLine = rawLine.length > 4000 ? `${rawLine.slice(0, 3997)}…` : rawLine;
    const segments = opts.wrapLongLines ? wrapLine(safeLine, mono, size, maxW) : [safeLine];

    segments.forEach((segment, segIdx) => {
      ensureSpace(lh + 6);

      const textX = layout.margin + gutter;

      if (opts.lineNumbers && segIdx === 0) {
        const num = `${String(idx + 1).padStart(4, ' ')}  `;
        page.drawText(num, {
          x: layout.margin,
          y,
          size: size * 0.85,
          font: mono,
          color: opts.theme === 'dark' ? rgb(0.45, 0.48, 0.52) : rgb(0.55, 0.58, 0.62),
        });
      }

      if (!opts.syntaxHighlight) {
        page.drawText(segment, {
          x: textX,
          y,
          size,
          font: mono,
          color: opts.theme === 'dark' ? rgb(0.92, 0.93, 0.95) : rgb(0.12, 0.13, 0.16),
        });
      } else {
        const pieces = segment.split(/(\s+)/).filter((p) => p.length);
        let x = textX;
        for (const tok of pieces) {
          const col = tokenColor(tok, opts.languageExtension, opts.theme, true);
          const tw = mono.widthOfTextAtSize(tok, size);
          if (x + tw > layout.margin + gutter + maxW) {
            y -= lh;
            ensureSpace(lh + 6);
            x = textX;
          }
          page.drawText(tok, { x, y, size, font: mono, color: col });
          x += tw;
        }
      }

      y -= lh;
    });
  });

  applyFooters(doc, fonts.regular, layout.margin);
  return doc.save();
}
